import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Gift, Download, Loader2, RotateCw, Filter } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "partner", label: "Partner" },
  { value: "beta", label: "Beta tester" },
  { value: "support_credit", label: "Support credit" },
  { value: "internal", label: "Internal" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(
  CATEGORY_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

// SuperAdmin — Comped-account log with reason notes, filters, and CSV export.
export default function SuperAdminComped() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/comp-logs", {
        params: {
          category: category || undefined,
          active_only: activeOnly || undefined,
          limit: 500,
        },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setActive(r.data.active || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load comped log");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, activeOnly]);

  const revoke = async (row) => {
    const note = window.prompt(`Revoke free access for "${row.tenant_name}"? (Optional note)`) ?? null;
    if (note === null) return;  // user pressed cancel
    try {
      await api.post(`/admin/tenants/${row.tenant_id}/revoke-comp`, {
        revoke_reason: note,
        new_status: "active",
      });
      toast.success("Comp revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const csvHref = useMemo(() => {
    if (!items.length) return null;
    const header = ["granted_at", "tenant_name", "tenant_id", "category", "reason", "actor_email", "revoked_at", "revoked_by", "revoke_reason"];
    const esc = (v) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return `"${s}"`;
    };
    const rows = items.map((r) => header.map((h) => esc(r[h])).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  }, [items]);

  return (
    <div className="ll-card-flat overflow-hidden" data-testid="sa-comp-card">
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
        <div className="flex items-center gap-2">
          <Gift size={14} />
          <div className="font-display font-bold">Comped accounts</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">
            {active} active · {total} total
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs">
            <Filter size={11} className="opacity-60" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="sa-comp-filter-category"
              className="px-2 py-1.5 border bg-white"
              style={{ borderColor: "var(--ll-border)" }}
            >
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              data-testid="sa-comp-active-only"
            />
            Active only
          </label>
          <a
            href={csvHref || "#"}
            download="comped-accounts.csv"
            onClick={(e) => { if (!csvHref) { e.preventDefault(); toast.info("No rows to export"); } }}
            data-testid="sa-comp-export"
            className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <Download size={11} /> Export CSV
          </a>
          <button onClick={load} className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1" style={{ borderColor: "var(--ll-border)" }} data-testid="sa-comp-refresh">
            <RotateCw size={11} /> Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="sa-comp-empty">
          <Gift size={24} className="mx-auto mb-2 opacity-40" />
          No comped accounts match this filter.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="sa-comp-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-4 py-2 font-medium">Granted</th>
              <th className="px-4 py-2 font-medium">Tenant</th>
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium">By</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const isActive = !r.revoked_at;
              return (
                <tr key={r.id} className="border-b hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">{fmtDate(r.granted_at)}</td>
                  <td className="px-4 py-2 text-xs">
                    <div className="font-medium">{r.tenant_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{r.tenant_id?.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-widest">
                    <span className="px-1.5 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }}>
                      {CATEGORY_LABEL[r.category] || r.category}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs max-w-md">{r.reason}</td>
                  <td className="px-4 py-2 text-xs text-[var(--ll-ink-soft)]">{r.actor_email}</td>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-widest">
                    {isActive ? (
                      <span className="px-1.5 py-0.5" style={{ background: "#DCFCE7", color: "#166534" }}>Active</span>
                    ) : (
                      <span className="px-1.5 py-0.5" style={{ background: "#F1F5F9", color: "#334155" }}>Revoked {fmtDate(r.revoked_at)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isActive && (
                      <button
                        onClick={() => revoke(r)}
                        data-testid={`sa-comp-revoke-${r.tenant_id}`}
                        className="text-[11px] px-2 py-1 border hover:bg-[#FEE2E2] hover:text-[#991B1B]"
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
